import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { OfficeBuildingIcon, PencilIcon } from "@heroicons/react/outline";
import { Field, Fieldset } from "@headlessui/react";
import SlideOver from "../../Modal/SlideOver";
import ExpandableDescription from "../ExpandableDescription";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormDate from "../../Form/FormDate";
import FormError from "../../Form/FormError";
import Button from "../../Button";
import {
  addLocalNotification,
  clearBusinessFixedAsset,
  requestDisposeFixedAsset,
  requestListAccount,
  requestListFixedAssetDepreciation,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useBusinessFixedAssetDispatch,
  useBusinessFixedAssetState,
  useNotificationDispatch,
} from "../../../../Context";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import { currencyFormat } from "../../../../Utils";

const DEFAULT_CASH_CODE = "110001";
const CASH_CATEGORIES = ["CASH_AND_BANK"];

const STATUS_STYLES = {
  ACTIVE: {
    badge: "bg-green-100 text-green-800",
    label: "fixedAssets.status.active",
  },
  FULLY_DEPRECIATED: {
    badge: "bg-blue-100 text-blue-800",
    label: "fixedAssets.status.fullyDepreciated",
  },
  DISPOSED: {
    badge: "bg-gray-100 text-gray-800",
    label: "fixedAssets.status.disposed",
  },
};

const FixedAssetDetailModal = ({
  open,
  onClose,
  onEdit,
  onDisposed,
  asset,
}) => {
  const { t, i18n } = useTranslation();

  const [disposing, setDisposing] = useState(false);
  const [proceeds, setProceeds] = useState("");
  const [disposalDate, setDisposalDate] = useState(new Date());
  const [cashAccount, setCashAccount] = useState(null);

  const notificationDispatch = useNotificationDispatch();
  const accountingDispatch = useAccountingDispatch();
  const dispatch = useBusinessFixedAssetDispatch();

  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const { accounts } = useAccountingState();
  const {
    depreciations,
    depreciationsLoading,
    disposeLoading,
    disposeError,
    disposeResult,
  } = useBusinessFixedAssetState();

  useEffect(() => {
    if (asset?.id) {
      requestListFixedAssetDepreciation(dispatch, { assetID: asset.id });
    }
    if (user?.mainBusiness?.id) {
      requestListAccount(accountingDispatch, {
        page: 1,
        businessID: user.mainBusiness.id,
        limit: 200,
        loadAll: true,
      });
    }
    return () => {
      clearBusinessFixedAsset(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id]);

  const cashOptions = useMemo(
    () =>
      (accounts || [])
        .filter((acc) => CASH_CATEGORIES.includes(acc.category))
        .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
        .map((acc) => ({
          value: acc.code,
          label: `${acc.code} - ${acc.name}`,
        })),
    [accounts],
  );

  useEffect(() => {
    if (cashOptions.length && !cashAccount) {
      setCashAccount(
        cashOptions.find((o) => o.value === DEFAULT_CASH_CODE) ||
          cashOptions[0],
      );
    }
  }, [cashOptions, cashAccount]);

  // Dispose success
  useEffect(() => {
    if (disposeResult) {
      const gainLoss = disposeResult.gainLoss ?? 0;
      addLocalNotification(notificationDispatch, {
        id: `dispose-${disposeResult.assetID || Date.now()}`,
        type: "success",
        title: "common.success",
        body: t("fixedAssets.notifications.disposeSuccess", {
          result:
            gainLoss >= 0
              ? `${t("fixedAssets.detail.gain")} ${currencyFormat(gainLoss)}`
              : `${t("fixedAssets.detail.loss")} ${currencyFormat(Math.abs(gainLoss))}`,
          interpolation: { escapeValue: false },
        }),
        showFull: true,
      });
      if (onDisposed) onDisposed();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disposeResult]);

  if (!asset) return null;

  const style = STATUS_STYLES[asset.status] || STATUS_STYLES.ACTIVE;
  const nbv =
    (asset.acquisitionCost || 0) - (asset.accumulatedDepreciation || 0);
  // The server refuses a disposal dated in the future, before the purchase, or
  // before the last month already depreciated; the picker offers only the rest.
  const earliestDisposal = [asset.acquisitionDate, asset.lastDepreciationDate]
    .filter(Boolean)
    .map((value) => dayjs(value).startOf("day"))
    .reduce((latest, day) => (day.isAfter(latest) ? day : latest), null);
  const monthly =
    asset.usefulLifeMonths > 0
      ? ((asset.acquisitionCost || 0) - (asset.residualValue || 0)) /
        asset.usefulLifeMonths
      : 0;
  const isDisposed = asset.status === "DISPOSED";

  const handleDispose = () => {
    if (writeBlocked) return;
    requestDisposeFixedAsset(dispatch, {
      businessID: user?.mainBusiness?.id,
      assetID: asset.id,
      proceeds: Number(proceeds || 0),
      disposalDate: dayjs(disposalDate).toISOString(),
      cashAccountCode: cashAccount?.value,
    });
  };

  const Row = ({ label, value }) => (
    <div className="sm:col-span-1">
      <dt className="text-sm font-medium text-gray-500">{t(label)}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value}</dd>
    </div>
  );

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t("fixedAssets.detail.title")}
      actionButton={
        onEdit &&
        !isDisposed && (
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            onClick={() => onEdit(asset)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        )
      }
    >
      <div className="space-y-6 mb-6">
        {/* Header */}
        <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:p-6 border border-gray-100">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                <OfficeBuildingIcon className="h-7 w-7 text-gray-500" />
              </div>
              <div>
                <ExpandableDescription
                  text={asset.name}
                  maxLength={40}
                  textClassName="text-xl font-semibold text-gray-900"
                />
                {asset.description && (
                  <ExpandableDescription
                    text={asset.description}
                    maxLength={60}
                    textClassName="text-sm text-gray-500"
                  />
                )}
              </div>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${style.badge}`}
            >
              {t(style.label)}
            </span>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            <Row
              label="fixedAssets.fields.acquisitionCost"
              value={currencyFormat(asset.acquisitionCost)}
            />
            <Row
              label="fixedAssets.fields.residualValue"
              value={currencyFormat(asset.residualValue)}
            />
            <Row
              label="fixedAssets.fields.usefulLifeMonths"
              value={asset.usefulLifeMonths}
            />
            <Row
              label="fixedAssets.fields.monthlyDepreciation"
              value={currencyFormat(monthly)}
            />
            <Row
              label="fixedAssets.fields.accumulatedDepreciation"
              value={currencyFormat(asset.accumulatedDepreciation)}
            />
            <Row
              label="fixedAssets.fields.netBookValue"
              value={
                <span className="font-semibold">{currencyFormat(nbv)}</span>
              }
            />
            <Row
              label="fixedAssets.fields.acquisitionDate"
              value={dayjs(asset.acquisitionDate)
                .locale(i18n.language)
                .format("DD MMM YYYY")}
            />
            <Row
              label="fixedAssets.fields.lastDepreciationDate"
              value={
                asset.lastDepreciationDate
                  ? dayjs(asset.lastDepreciationDate)
                      .locale(i18n.language)
                      .format("MMM YYYY")
                  : "-"
              }
            />
          </dl>
        </div>

        {/* Accounts */}
        <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              {t("fixedAssets.detail.accounts")}
            </h4>
          </div>
          <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3">
            <Row
              label="fixedAssets.fields.assetAccount"
              value={asset.assetAccountCode}
            />
            <Row
              label="fixedAssets.fields.accumulatedAccount"
              value={asset.accumulatedAccountCode}
            />
            <Row
              label="fixedAssets.fields.expenseAccount"
              value={asset.expenseAccountCode}
            />
            <Row
              label="fixedAssets.fields.fundingAccount"
              value={asset.fundingAccountCode || "-"}
            />
            <Row
              label="fixedAssets.fields.acquisitionJournal"
              value={
                asset.acquisitionTransactionID
                  ? t("fixedAssets.detail.journalCreated")
                  : t("fixedAssets.detail.journalSkipped")
              }
            />
          </dl>
        </div>

        {/* Depreciation schedule */}
        <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
              {t("fixedAssets.detail.schedule")}
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    {t("fixedAssets.fields.period")}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    {t("fixedAssets.fields.amount")}
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    {t("fixedAssets.fields.accumulated")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {depreciationsLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-3 text-sm text-gray-500 text-center"
                    >
                      {t("common.loading")}...
                    </td>
                  </tr>
                ) : depreciations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-3 text-sm text-gray-500 text-center"
                    >
                      {t("fixedAssets.detail.noSchedule")}
                    </td>
                  </tr>
                ) : (
                  depreciations.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {dayjs(row.periodDate)
                          .locale(i18n.language)
                          .format("MMM YYYY")}
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-700">
                        {currencyFormat(row.amount)}
                      </td>
                      <td className="px-4 py-2 text-sm text-right text-gray-700">
                        {currencyFormat(row.accumulatedAfter)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Disposal */}
        {isDisposed ? (
          <div className="bg-gray-50 px-4 py-4 sm:px-6 rounded-lg border border-gray-100">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <Row
                label="fixedAssets.fields.disposalDate"
                value={
                  asset.disposalDate
                    ? dayjs(asset.disposalDate)
                        .locale(i18n.language)
                        .format("DD MMM YYYY")
                    : "-"
                }
              />
              <Row
                label="fixedAssets.fields.disposalProceeds"
                value={currencyFormat(asset.disposalProceeds || 0)}
              />
            </dl>
          </div>
        ) : (
          // Disposal posts a journal, so it follows the same write-block as
          // every other input surface.
          !writeBlocked && (
            <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
              <div className="px-4 py-4 sm:px-6 border-b border-gray-100 flex justify-between items-center">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  {t("fixedAssets.detail.disposal")}
                </h4>
                {!disposing && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setDisposing(true)}
                  >
                    {t("fixedAssets.actions.dispose")}
                  </Button>
                )}
              </div>
              {disposing && (
                <div className="px-4 py-4 sm:px-6 space-y-4">
                  <FormError error={disposeError} />
                  <Fieldset
                    disabled={disposeLoading}
                    className="space-y-4 data-[disabled]:opacity-50"
                  >
                    <Field>
                      <FormInput
                        label="fixedAssets.fields.proceeds"
                        id="proceeds"
                        name="proceeds"
                        type="number"
                        min="0"
                        prefix="Rp"
                        value={proceeds}
                        onChange={(e) => setProceeds(e.target.value)}
                        placeholder={t("fixedAssets.placeholders.proceeds")}
                      />
                    </Field>
                    <FormDate
                      label="fixedAssets.fields.disposalDate"
                      selected={disposalDate}
                      onChange={(date) => setDisposalDate(date)}
                      minDate={earliestDisposal?.toDate()}
                      maxDate={new Date()}
                    />
                    <Field>
                      <FormSelect
                        searchable
                        label="fixedAssets.fields.cashAccount"
                        id="cash-account"
                        name="cash_account"
                        options={cashOptions}
                        value={cashAccount}
                        onChange={(opt) => setCashAccount(opt)}
                        placeholder={t("fixedAssets.placeholders.cashAccount")}
                      />
                    </Field>
                    <div className="flex justify-end space-x-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setDisposing(false)}
                      >
                        {t("common.cancel")}
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        loading={disposeLoading}
                        onClick={handleDispose}
                      >
                        {t("fixedAssets.actions.confirmDispose")}
                      </Button>
                    </div>
                  </Fieldset>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </SlideOver>
  );
};

export default FixedAssetDetailModal;
